import { Panel } from "@bryntum/schedulerpro";
import {
  getImportTypeUndoPreview,
  undoImport
} from "../../../network/network.jsx";
import {
  showConfirmationDialog,
  showToast
} from "../../../other/utils/utils.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { IMPORT_TYPES_HISTORY } from "../../../other/constants/OtherKeys.jsx";
import {
  cellMenuItemRemoveRow,
  processCellMenuItems
} from "../../../other/utils/gridCellMenuUtils.jsx";
import { pluralize } from "../../../other/utils/otherUtils.jsx";
import { shouldShowAsyncDialog } from "../AsyncTaskMessageEditor/SwivuAsyncMessagePopup.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";

export default class HistoryTab extends Panel {
  static get $name() { return "HistoryTab"; }
  static get type() { return "historytab"; }

  static get configurable() {
    return {
      title: "History",
      cls: "[&_div.b-historytab-content]:!p-0",
      items: {
        historyTabGrid: {
          type: WIDGET_KEYS.swivuPreferenceStyleGrid,
          height: "100%",
          listeners: {
            beforeSelectionChange: "up.beforeSelectionChange",
            beforeRenderRow: "up.beforeRenderRow"
          },
          features: {
            sort: false,
            group: false,
            filter: false,
            headerMenu: false,
            multiSelect: false,
            cellMenu: {
              items: {
                cut: false,
                copy: false,
                paste: false,
                removeRow: cellMenuItemRemoveRow({ onItem: "up.onRemove" }),
                undo: {
                  text: "Undo Import",
                  icon: "b-icon b-icon-undo",
                  cls: "b-raised b-red",
                  onItem: "up.onUndoClick",
                  hidden: false
                }
              }
            }
          },
          columns: [
            { id: "file-name", field: "fileName", text: "File Name", flex: 1, editor: false, htmlEncode: false,
              renderer: ({ record, value }) => `<a href="${record.fileUrl}" download onclick="event.stopPropagation();">${value}</a>` },
            { id: "record-count", field: "recordsImported", text: "Record Count", flex: 1, editor: false },
            { id: "import-date-time", field: "importDate", type: "date", text: "Import Date & Time", flex: 1, editor: false },
            { id: "imported-by", field: "revertedByUserName", text: "Imported By", flex: 1, editor: false }
          ],
          bbar: {
            items: {
              undoButton: {
                type: "button",
                text: "Undo Import",
                cls: "text-[#048ded] shrink-0 disabled:opacity-[0.3]",
                icon: "b-icon b-icon-undo",
                onClick: "up.onUndoButtonClick",
                disabled: false,
              }
            }
          }
        }
      }
    };
  }

  editRecord(record, context) {
    this.context = context;
    const { historyTabGrid } = this.widgetMap;
    const { undoButton } = historyTabGrid.bbar.widgetMap;
    historyTabGrid.store = context.project.importBatchStore.chain(
      (storeRecord) => storeRecord.entityType === IMPORT_TYPES_HISTORY[record.importType] && this.isValidHistoryRecord(storeRecord)
    );

    historyTabGrid.features.cellMenu.processItems = processCellMenuItems({ grid: historyTabGrid, user: context.user });
    undoButton.disabled = !historyTabGrid.selectedRecord;
  }

  beforeRenderRow({row, record}) {
    row.cls = { ...row.cls, 'opacity-[0.67]': record.isReverted };
  }

  /**
   * Checks if a record should be kept in history
   * @param {Object} record - The import batch record
   * @returns {Boolean} - True if the record has at least one successful import
   */
  isValidHistoryRecord(record) {
    // Keep only records that have at least one successful import
    return record.recordsImported > 0;
  }

  onRemove({ record }) {
    this.deleteConfirmationDialog(record, "Do you want to delete the import history of this record?");
  }

  beforeSelectionChange({ selected }) {
    if (selected.length) this.toggleContextMenu(selected[0]);
  }

  /**
   * Updates the context menu and toolbar button states based on the selected record
   * @param {Object} record - The selected record
   */
  toggleContextMenu(record) {
    const { historyTabGrid } = this.widgetMap;
    const { undoButton } = historyTabGrid.bbar.widgetMap;
    const { undo, removeRow } = historyTabGrid.features.cellMenu.items;

    // Determine if undo should be disabled based on record state
    const shouldDisableUndo = !record || record.isReverted || record.recordsImported === 0;

    undo.hidden = shouldDisableUndo;
    undoButton.disabled = shouldDisableUndo;
    removeRow.hidden = !shouldDisableUndo;
  }

  /**
   * Shows a confirmation dialog for deleting a record
   * @param {Object} record - The record to delete
   * @param {String} msg - The message to display in the dialog
   */
  deleteConfirmationDialog(record, msg) {
    const { historyTabGrid } = this.widgetMap;

    showConfirmationDialog({
      title: "Delete Confirmation",
      message: msg,
      cancelBtnText: "Cancel",
      confirmBtnText: "Delete",
      isCloseByHeader: true,
    }).then((isDeleteConfirmed) => {
      if (isDeleteConfirmed) {
        // Show loading mask while deleting
        historyTabGrid.mask('Deleting record...');

        // Remove the record
        record.remove();

        // Remove the mask
        historyTabGrid.unmask();

        // Show success message
        showToast({ msg: "Record deleted successfully", type: "success" });
      }
    }).catch(() => {
      // Make sure to remove the mask if there's an error
      historyTabGrid.unmask();
    });
  }

  /**
   * Handler for the undo button in the cell menu
   * @param {Object} param0 - Event parameters including the record
   */
  onUndoClick({ record }) {
    this.performUndoAction(record);
  }

  /**
   * Handler for the undo button in the bottom toolbar
   */
  onUndoButtonClick() {
    const { historyTabGrid } = this.widgetMap;
    const selectedRecord = historyTabGrid.selectedRecord;

    if (selectedRecord) {
      this.performUndoAction(selectedRecord);
    } else {
      showToast({ msg: "Please select a record to undo", type: "warning" });
    }
  }

  /**
   * Performs the undo action for a given record
   * @param {Object} record - The record to undo
   */
  performUndoAction(record) {
    const { historyTabGrid } = this.widgetMap;
    // Show loading mask while fetching undoable records
    historyTabGrid.mask('Undoing changes...');
    getImportTypeUndoPreview(record.id).then(({data}) => {
      // Remove the mask once data is loaded
      historyTabGrid.unmask();
      if (data.revertible_records === 0) {
        this.deleteConfirmationDialog(record, "All records in this import are changed, there is nothing to undo. <br/> Do you want to delete this record?");
      } else {
        showConfirmationDialog({
          title: `Undoable Records (${data.revertible_records})`,
          message: `<div>There are ${data.revertible_records} undoable ${pluralize(data.revertible_records, "record")}. Do you want to continue?</div>`,
          cancelBtnText: "Cancel",
          confirmBtnText: { cls: "order-1 b-raised", color: "b-blue", text: "Undo" },
          isCloseByHeader: true
        }).then((isUndoConfirmed) => {
          if (isUndoConfirmed) {
            // Show async task message dialog for undo import
            const showDialog = shouldShowAsyncDialog('UNDO_CSV_IMPORT');
            if (showDialog && this.context && this.context.editorHandler) {
              this.context.editorHandler.openEditor(
                EDITOR_KEYS.asyncTaskMessageEditor,
                null,
                { record: "UNDO_CSV_IMPORT" },
                { useExisting: true, by: "recordType" }
              );
            }
            // Show loading mask while performing undo
            historyTabGrid.mask('Undoing import...');
            undoImport(record.id).then(() => {
              // Remove the mask once undo is complete
              historyTabGrid.unmask();
              showToast({msg: "Undo Import started successfully...", type: "success"});
            }).catch(() => {
              historyTabGrid.unmask();
              showToast({ msg: "Undo failed", type: "error" });
            });
          }
        })
      }
    }).catch(() => {
      historyTabGrid.unmask();
      showToast({ msg: "Undo failed", type: "error" });
    });
  }
}

HistoryTab.initClass();